import React from "react";
import { connect } from "react-redux";
import { FaUniversity, FaPlus, FaTrashAlt, FaPen, FaQrcode } from "react-icons/fa";
import alertify from "alertifyjs";
import { Translate, I18n } from "react-redux-i18n";
import Modal from "./Modal";
import * as actions_bank from "../redux/actions/bankAccountActions";
import "./styles/PanelBankAccounts.css";
import "./styles/Alertify.css";
import "./styles/AlertifyTheme.css";
import "./styles/Alertify.min.css";
import "./styles/AlertifyTheme.min.css";

const ACCOUNT_TYPES = ["savings", "checking"];

const emptyForm = () => ({
  id: null,
  holder_name: "",
  identification: "",
  bank: "",
  account_number: "",
  account_type: "",
  qr: null,
  qrPreview: "",
  remove_qr: false,
});

const formFromAccount = (item) => ({
  id: item.Id,
  holder_name: item.HolderName || "",
  identification: item.Identification || "",
  bank: item.Bank || "",
  account_number: item.AccountNumber || "",
  account_type: item.AccountType || "",
  qr: null,
  qrPreview: item.QrUrl || "",
  remove_qr: false,
});

class PanelBankAccounts extends React.Component {
  state = {
    modalOpen: false,
    editingId: null,
    form: emptyForm(),
  };

  componentDidMount() {
    this.props.LoadAccounts(this.props.user_id, this.props.token);
  }

  openCreate = () => {
    const max = this.props.max || 5;
    if ((this.props.accountList || []).length >= max) {
      alertify.warning(I18n.t("bank_account_max"));
      return;
    }
    this.setState({ modalOpen: true, editingId: null, form: emptyForm() });
  };

  openEdit = (item) => {
    this.setState({
      modalOpen: true,
      editingId: item.Id,
      form: formFromAccount(item),
    });
  };

  closeModal = () => {
    this.setState({ modalOpen: false });
  };

  onChangeField = (field) => (event) => {
    this.setState({ form: { ...this.state.form, [field]: event.target.value } });
  };

  onChangeQr = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alertify.warning(I18n.t("file_wrong"));
      return;
    }

    this.setState({
      form: {
        ...this.state.form,
        qr: file,
        qrPreview: URL.createObjectURL(file),
        remove_qr: false,
      },
    });
  };

  removeQr = () => {
    this.setState({
      form: { ...this.state.form, qr: null, qrPreview: "", remove_qr: true },
    });
  };

  isValid = () => {
    const { form } = this.state;
    return (
      form.holder_name.trim() &&
      form.identification.trim() &&
      form.bank.trim() &&
      form.account_number.trim() &&
      form.account_type
    );
  };

  onSubmit = async () => {
    if (!this.isValid()) {
      alertify.warning(I18n.t("bank_account_required"));
      return;
    }

    const { form, editingId } = this.state;
    const params = {
      account_id: editingId,
      holder_name: form.holder_name.trim(),
      identification: form.identification.trim(),
      bank: form.bank.trim(),
      account_number: form.account_number.trim(),
      account_type: form.account_type,
      qr: form.qr,
      remove_qr: form.remove_qr,
    };

    const result = editingId
      ? await this.props.UpdateAccount(this.props.user_id, this.props.token, params)
      : await this.props.CreateAccount(this.props.user_id, this.props.token, params);

    if (result && result.ok) {
      alertify.success(I18n.t("saved_succesfull"));
      this.closeModal();
    } else {
      alertify.warning(I18n.t((result && result.error) || "try_later"));
    }
  };

  onDelete = (item) => {
    alertify.confirm(
      I18n.t("bank_account_delete_title"),
      I18n.t("bank_account_delete_confirm"),
      async () => {
        const result = await this.props.RemoveAccount(
          this.props.user_id,
          this.props.token,
          item.Id
        );
        if (result && result.ok) {
          alertify.success(I18n.t("saved_succesfull"));
        } else {
          alertify.warning(I18n.t("try_later"));
        }
      },
      function () {}
    );
  };

  render() {
    const list = this.props.accountList || [];
    const max = this.props.max || 5;
    const canAdd = list.length < max;
    const { form } = this.state;

    return (
      <section className="bank-accounts">
        <div className="bank-accounts__header">
          <div>
            <h4 className="bank-accounts__title">
              <Translate value="bank_account_title" />
            </h4>
            <p className="bank-accounts__hint">
              <Translate value="bank_account_hint" /> ({list.length}/{max})
            </p>
          </div>
          <button
            type="button"
            className="bank-accounts__add"
            onClick={this.openCreate}
            disabled={!canAdd}
          >
            <FaPlus />
            <Translate value="bank_account_add" />
          </button>
        </div>

        {list.length === 0 ? (
          <div className="bank-accounts__empty">
            <FaUniversity />
            <p>
              <Translate value="bank_account_empty" />
            </p>
          </div>
        ) : (
          <ul className="bank-account-list">
            {list.map((item) => (
              <li className="bank-account-list__item" key={item.Id}>
                <div className="bank-account-list__icon">
                  <FaUniversity />
                </div>
                <div className="bank-account-list__info">
                  <div className="bank-account-list__bank">{item.Bank}</div>
                  <div className="bank-account-list__holder">
                    {item.HolderName}
                  </div>
                  <div className="bank-account-list__number">
                    {item.AccountNumber}
                    {item.AccountType ? (
                      <span className="bank-account-list__type">
                        {I18n.t("bank_account_type_" + item.AccountType)}
                      </span>
                    ) : null}
                  </div>
                </div>
                {item.QrUrl ? (
                  <a
                    className="bank-account-list__qr"
                    href={item.QrUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={item.QrUrl} alt="QR" />
                  </a>
                ) : null}
                <div className="bank-account-list__actions">
                  <button
                    type="button"
                    className="bank-account-list__btn bank-account-list__btn--edit"
                    onClick={() => this.openEdit(item)}
                    aria-label={I18n.t("profile_edit")}
                  >
                    <FaPen />
                  </button>
                  <button
                    type="button"
                    className="bank-account-list__btn bank-account-list__btn--delete"
                    onClick={() => this.onDelete(item)}
                    aria-label={I18n.t("bank_account_delete_title")}
                  >
                    <FaTrashAlt />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <Modal
          isOpen={this.state.modalOpen}
          onClose={this.closeModal}
          title={
            this.state.editingId ? (
              <Translate value="bank_account_edit" />
            ) : (
              <Translate value="bank_account_add" />
            )
          }
        >
          <div className="bank-account-form">
            <div className="bank-account-form__field">
              <label>
                {I18n.t("bank_account_holder")}{" "}
                <span className="bank-account-form__req">*</span>
              </label>
              <input
                type="text"
                className="form-control profile-edit-input"
                placeholder={I18n.t("bank_account_holder")}
                value={form.holder_name}
                onChange={this.onChangeField("holder_name")}
                maxLength={120}
              />
            </div>

            <div className="bank-account-form__row">
              <div className="bank-account-form__field">
                <label>
                  {I18n.t("bank_account_identification")}{" "}
                  <span className="bank-account-form__req">*</span>
                </label>
                <input
                  type="text"
                  className="form-control profile-edit-input"
                  placeholder={I18n.t("bank_account_identification")}
                  value={form.identification}
                  onChange={this.onChangeField("identification")}
                  maxLength={60}
                />
              </div>

              <div className="bank-account-form__field">
                <label>
                  {I18n.t("bank_account_type")}{" "}
                  <span className="bank-account-form__req">*</span>
                </label>
                <select
                  className="form-control profile-edit-input"
                  value={form.account_type}
                  onChange={this.onChangeField("account_type")}
                >
                  <option value="">{I18n.t("profile_select")}</option>
                  {ACCOUNT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {I18n.t("bank_account_type_" + type)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bank-account-form__row">
              <div className="bank-account-form__field">
                <label>
                  {I18n.t("bank_account_bank")}{" "}
                  <span className="bank-account-form__req">*</span>
                </label>
                <input
                  type="text"
                  className="form-control profile-edit-input"
                  placeholder={I18n.t("bank_account_bank")}
                  value={form.bank}
                  onChange={this.onChangeField("bank")}
                  maxLength={100}
                />
              </div>

              <div className="bank-account-form__field">
                <label>
                  {I18n.t("bank_account_number")}{" "}
                  <span className="bank-account-form__req">*</span>
                </label>
                <input
                  type="text"
                  className="form-control profile-edit-input"
                  placeholder={I18n.t("bank_account_number")}
                  value={form.account_number}
                  onChange={this.onChangeField("account_number")}
                  maxLength={60}
                />
              </div>
            </div>

            <div className="bank-account-form__field">
              <label>
                {I18n.t("bank_account_qr")}{" "}
                <span className="bank-account-form__optional">
                  ({I18n.t("optional")})
                </span>
              </label>

              {form.qrPreview ? (
                <div className="bank-account-form__qr-preview">
                  <img src={form.qrPreview} alt="QR" />
                  <button
                    type="button"
                    className="bank-account-form__qr-remove"
                    onClick={this.removeQr}
                  >
                    <FaTrashAlt />
                  </button>
                </div>
              ) : (
                <label className="bank-account-form__qr-upload">
                  <FaQrcode />
                  <span>
                    <Translate value="bank_account_qr_upload" />
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={this.onChangeQr}
                    hidden
                  />
                </label>
              )}
            </div>

            <div className="bank-account-form__footer">
              <button
                type="button"
                className="bank-account-form__btn bank-account-form__btn--cancel"
                onClick={this.closeModal}
              >
                <Translate value="close" />
              </button>
              <button
                type="button"
                className="bank-account-form__btn bank-account-form__btn--save"
                onClick={this.onSubmit}
                disabled={this.props.saving}
              >
                {this.props.saving ? "..." : I18n.t("save")}
              </button>
            </div>
          </div>
        </Modal>
      </section>
    );
  }
}

function mapStateToProps(reducer) {
  return {
    user_id: reducer.session.auth.id,
    token: reducer.session.auth.access_token,
    accountList: reducer.bankAccount.list,
    max: reducer.bankAccount.max,
    loading: reducer.bankAccount.loading,
    saving: reducer.bankAccount.saving,
  };
}

const mapDispatchToProps = (dispatch) => ({
  LoadAccounts: (user_id, token) =>
    dispatch(actions_bank.loadList({ user_id, token })),
  CreateAccount: (user_id, token, params) =>
    dispatch(actions_bank.newItem({ user_id, token, params })),
  UpdateAccount: (user_id, token, params) =>
    dispatch(actions_bank.updateItem({ user_id, token, params })),
  RemoveAccount: (user_id, token, account_id) =>
    dispatch(actions_bank.removeItem({ user_id, token, account_id })),
});

export default connect(mapStateToProps, mapDispatchToProps)(PanelBankAccounts);
